import { atom } from 'jotai'
import { atomFamily } from 'jotai/utils'

/** 解析失敗時に表示する技術情報を含まない共通文言。 */
export const assistantAnalysisFailedMessage =
  '解析中にエラーが発生しました。取引データは変更されていません。再度お試しください。'

/** portfolioごとの取引履歴アシスタント解析状態。 */
export type AssistantAnalysisState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading-actions' }
  | {
      readonly status: 'fetching-token-counts'
      readonly completed: number
      readonly total: number
    }
  | { readonly status: 'evaluating-rules' }
  | { readonly status: 'saving-proposals' }
  | {
      readonly status: 'completed'
      readonly diagnostics?: Readonly<Record<string, number>>
      readonly proposalCount: number
      readonly checkedActionCount: number
      readonly unavailableSourceCount: number
      readonly completedAt: number
    }
  | {
      readonly status: 'failed'
      readonly message: string
    }

/** portfolio UUIDごとの解析状態を保持するsession内Map。 */
export const assistantAnalysisStatesAtom = atom<
  ReadonlyMap<string, AssistantAnalysisState>
>(new Map())

/** portfolio UUIDごとのraw pending proposal countを保持するsession内Map。 */
export const assistantPendingProposalCountsAtom = atom<
  ReadonlyMap<string, number>
>(new Map())

/** 指定portfolioの解析状態を読み書きするatom family。 */
export const assistantAnalysisStateAtomFamily = atomFamily(
  (portfolioId: string) =>
    atom(
      (get): AssistantAnalysisState =>
        get(assistantAnalysisStatesAtom).get(portfolioId) ?? {
          status: 'idle',
        },
      (get, set, nextState: AssistantAnalysisState) => {
        const nextStates = new Map(get(assistantAnalysisStatesAtom))
        nextStates.set(portfolioId, nextState)
        set(assistantAnalysisStatesAtom, nextStates)
      },
    ),
)

/** 指定portfolioのraw pending proposal countを読み書きするatom family。 */
export const assistantPendingProposalCountAtomFamily = atomFamily(
  (portfolioId: string) =>
    atom(
      (get) => get(assistantPendingProposalCountsAtom).get(portfolioId) ?? 0,
      (get, set, nextCount: number) => {
        if (!Number.isInteger(nextCount) || nextCount < 0) {
          throw new Error(
            'assistant pending proposal count must be nonnegative',
          )
        }
        const nextCounts = new Map(get(assistantPendingProposalCountsAtom))
        nextCounts.set(portfolioId, nextCount)
        set(assistantPendingProposalCountsAtom, nextCounts)
      },
    ),
)
